import {inject, Injectable} from '@angular/core';
import {catchError, map, Observable, of, switchMap, tap} from 'rxjs';
import {NavigationService} from '../../shared/utils/navigation.service';
import {NachrichtSummary} from '../model/nachricht/nachricht';
import i18next from 'i18next';
import {getPdfDateTimeFormat} from '../../shared/utils/date-time.helper';
import {NachrichtenService} from './apiservices/nachrichten.service';
import {NachrichtActionHandler} from '../../shared/components/nachricht-action-modals/nachricht-action-handler';
import {NachrichtAction} from '../../shared/components/nachricht-action-buttons/nachricht-action.enum';
import {getOrdnerEnum, OrdnerEnum} from '../model/ordner.enum';
import {ModalComponent} from '../../shared/components/modal/modal.component';
import {MODAL_OPTIONS_REQUIRE_CONFIRM} from "../../shared/components/modal/modal.options";
import {PdfKitService} from "../pdf/pdf-kit.service";

@Injectable({ providedIn: 'root' })
export class NachrichtActionService implements NachrichtActionHandler {

    private nav = inject(NavigationService);
    private nachrichtenService = inject(NachrichtenService);
    private pdfKitService = inject(PdfKitService);

    private modals: Record<string, ModalComponent> = {};

    registerModal(name: string, ref: ModalComponent): void {
        this.modals[name] = ref;
    }

    handleAction(
        action: NachrichtAction,
        nachricht: NachrichtSummary,
        confirmed?: boolean
    ): Observable<OrdnerEnum> {
        switch (action) {
            case NachrichtAction.REPLY:
                return this.handleReply(nachricht);
            case NachrichtAction.EDIT:
                return this.handleEdit(nachricht);
            case NachrichtAction.DOWNLOAD:
                return this.handleDownload(nachricht);
            case NachrichtAction.RESTORE:
                return this.handleRestored(nachricht, !!confirmed);
            case NachrichtAction.DELETE:
                return this.handleDeleted(nachricht, !!confirmed);
            case NachrichtAction.DELETE_IRREVOCABLE:
                return this.handleDeleteIrrevocable(nachricht, !!confirmed);
            default:
                return of(undefined as unknown as OrdnerEnum);
        }
    }

    handleReply(nachricht: NachrichtSummary): Observable<OrdnerEnum> {
        this.nav.goTo('/antwort/' + nachricht.nachrichtId);
        return of(undefined as unknown as OrdnerEnum);
    }

    handleEdit(nachricht: NachrichtSummary): Observable<OrdnerEnum> {
        this.nav.goTo('/entwurf/' + nachricht.nachrichtId);
        return of(undefined as unknown as OrdnerEnum);
    }

    handleDownload(nachricht: NachrichtSummary): Observable<OrdnerEnum> {
        return this.nachrichtenService
            .getNachricht(nachricht.ordnerType.toLowerCase(), nachricht.nachrichtId)
            .pipe(
                switchMap((full) => {
                    if (!full) {
                        return of(undefined as OrdnerEnum);
                    }
                    const fileName =
                        `${i18next.t('download.title').replace(/\s+/g, '-').toLowerCase()}-${getPdfDateTimeFormat(full.datum)}.pdf`;
                    return this.pdfKitService.downloadNachrichtPdf(full, fileName).pipe(
                        map((): OrdnerEnum => undefined as OrdnerEnum)
                    );
                }),
                catchError(() => of(undefined as OrdnerEnum))
            );
    }

    handleRestored(
        nachricht: NachrichtSummary,
        confirmed: boolean
    ): Observable<OrdnerEnum> {
        const ordner = getOrdnerEnum(nachricht.ordnerType);
        if (!confirmed) {
            return this.nachrichtenService.restoreNachricht(nachricht.nachrichtId).pipe(
                tap(() => this.modals['RESTORE-success']?.open(MODAL_OPTIONS_REQUIRE_CONFIRM)),
                map(() => undefined as unknown as OrdnerEnum),
                catchError(() => {
                    this.modals['RESTORE-error']?.open();
                    return of(undefined as unknown as OrdnerEnum);
                })
            );
        }
        return of(ordner);
    }

    handleDeleted(
        nachricht: NachrichtSummary,
        confirmed: boolean
    ): Observable<OrdnerEnum> {
        const ordner = getOrdnerEnum(nachricht.ordnerType);
        if (!confirmed) {
            return this.nachrichtenService.deleteNachricht(ordner, nachricht.nachrichtId).pipe(
                tap(() => this.modals['DELETE-success']?.open(MODAL_OPTIONS_REQUIRE_CONFIRM)),
                map(() => undefined as unknown as OrdnerEnum),
                catchError(() => {
                    this.modals['DELETE-error']?.open();
                    return of(undefined as unknown as OrdnerEnum);
                })
            );
        }
        return of(ordner);
    }

    handleDeleteIrrevocable(
        nachricht: NachrichtSummary,
        confirmed: boolean
    ): Observable<OrdnerEnum> {
        if (!confirmed) {
            this.modals['DELETE_IRREVOCABLE-confirm']?.open();
            return of(undefined as unknown as OrdnerEnum);
        }
        return this.nachrichtenService.deleteNachrichtIrrevocable(nachricht.nachrichtId).pipe(
            tap(() => this.modals['DELETE_IRREVOCABLE-success']?.open()),
            map(() => OrdnerEnum.GELOESCHT as OrdnerEnum),
            catchError(() => {
                this.modals['DELETE_IRREVOCABLE-error']?.open();
                return of(undefined as unknown as OrdnerEnum);
            })
        );
    }
}